import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Wrench, Timer, TrendingUp, TrendingDown, Minus, Pause } from 'lucide-react';
import { useMinuteProductivityMetrics } from '@/hooks/useMinuteProductivityMetrics';
import { useShopTimezone } from '@/contexts/TimezoneContext';

interface MinuteProductivityCardProps {
  shopId: string;
  technicianProfileId?: string; // undefined = shop-wide
  startDate: string;
  endDate: string;
}

export function MinuteProductivityCard({
  shopId,
  technicianProfileId,
  startDate,
  endDate,
}: MinuteProductivityCardProps) {
  const { shopTimezone } = useShopTimezone();
  const { data: metrics, isLoading, isError } = useMinuteProductivityMetrics({
    shopId,
    technicianProfileId,
    startDate,
    endDate,
    shopTimezone,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Minute Productivity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Core-100 R4 (CORE-C): a failed read is said as a failure, never as "No data available".
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Minute Productivity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8" data-testid="minute-productivity-failed">
            Minute productivity could not be loaded. This is not a statement that there is no data.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Minute Productivity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">No data available</p>
        </CardContent>
      </Card>
    );
  }

  const getOverUnderIcon = () => {
    if (metrics.overUnderMinutes > 0) return <TrendingUp className="h-4 w-4 text-success" />;
    if (metrics.overUnderMinutes < 0) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getUtilizationColor = (pct: number) => {
    if (pct >= 80) return 'text-success';
    if (pct >= 60) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-muted-foreground" />
            Minute Productivity
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {metrics.dataSource === 'time_clock' ? 'From Clock' : 'Estimated'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Available Minutes */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Available</span>
            </div>
            <p className="text-2xl font-bold">{metrics.availableMinutes.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">minutes ({(metrics.availableMinutes / 60).toFixed(1)} hrs)</p>
            {/*
              CORE-AW §44: technician-days with no recorded hours contribute
              nothing to this figure. Printing it unqualified would present a
              partial count as the shop's available time.
            */}
            {!metrics.capacityComplete && (
              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="productivity-capacity-incomplete"
              >
                Excludes {metrics.unknownCapacityDays} technician-day
                {metrics.unknownCapacityDays === 1 ? '' : 's'} with no working hours set
              </p>
            )}
          </div>

          {/* Booked Minutes */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-4 w-4 text-accent" />
              <span className="text-xs text-muted-foreground">Booked</span>
            </div>
            <p className="text-2xl font-bold">{metrics.bookedMinutes.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">minutes ({(metrics.bookedMinutes / 60).toFixed(1)} hrs)</p>
          </div>

          {/* Wrenching Minutes */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4 text-success" />
              <span className="text-xs text-muted-foreground">Wrenching</span>
            </div>
            <p className="text-2xl font-bold">{metrics.wrenchingMinutes.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">minutes ({(metrics.wrenchingMinutes / 60).toFixed(1)} hrs)</p>
          </div>

          {/* Utilization % */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Utilization</span>
            </div>
            {/*
              CORE-AW §44: a percentage of an unknown denominator is not a
              small number, it is not a number. Rendering 0% here said the
              shop worked none of the time it had, from a record that never
              said how much time that was.
            */}
            <p
              className={`text-2xl font-bold ${metrics.utilizationPct === null ? 'text-muted-foreground' : getUtilizationColor(metrics.utilizationPct)}`}
              data-testid="productivity-utilization"
            >
              {metrics.utilizationPct === null ? '—' : `${metrics.utilizationPct}%`}
            </p>
            {metrics.utilizationPct === null ? (
              <p className="text-xs text-muted-foreground mt-2">
                Working hours not set — capacity unknown
              </p>
            ) : (
              <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full ${
                    metrics.utilizationPct >= 80 ? 'bg-success' :
                    metrics.utilizationPct >= 60 ? 'bg-warning' : 'bg-destructive'
                  }`}
                  style={{ width: `${Math.min(metrics.utilizationPct, 100)}%` }}
                />
              </div>
            )}
          </div>

          {/* Book Load % */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-4 w-4 text-accent" />
              <span className="text-xs text-muted-foreground">Book Load</span>
            </div>
            <p className="text-2xl font-bold" data-testid="productivity-book-load">
              {metrics.bookLoadPct === null ? '—' : `${metrics.bookLoadPct}%`}
            </p>
            {metrics.bookLoadPct === null ? (
              <p className="text-xs text-muted-foreground mt-2">
                Working hours not set — capacity unknown
              </p>
            ) : (
              <>
                <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                  <div
                    className="h-1.5 rounded-full bg-primary"
                    style={{ width: `${Math.min(metrics.bookLoadPct, 100)}%` }}
                  />
                </div>
                {!metrics.capacityComplete && (
                  <p className="text-xs text-muted-foreground mt-1">of known capacity</p>
                )}
              </>
            )}
          </div>

          {/* Over/Under vs Book */}
          <div className="p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              {getOverUnderIcon()}
              <span className="text-xs text-muted-foreground">vs Book</span>
            </div>
            <p className={`text-2xl font-bold ${
              metrics.overUnderMinutes > 0 ? 'text-success' :
              metrics.overUnderMinutes < 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {metrics.overUnderMinutes > 0 ? '+' : ''}{metrics.overUnderMinutes}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.overUnderMinutes > 0 ? 'ahead' : metrics.overUnderMinutes < 0 ? 'behind' : 'on target'}
            </p>
          </div>
        </div>

        {/* Additional info row */}
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Pause className="h-3 w-3" />
            <span>Pause: {metrics.pauseMinutes} min ({(metrics.pauseMinutes / 60).toFixed(1)} hrs)</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span data-testid="productivity-idle">
              {metrics.idleMinutes === null
                ? 'Idle: unknown — capacity not established'
                : `Idle: ${metrics.idleMinutes} min (${(metrics.idleMinutes / 60).toFixed(1)} hrs)`}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
